import React, { useEffect, useState } from "react";
import { Loader2, Check, EyeOff, Trash2, ExternalLink, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { adminListReviews, adminUpdateReview, adminDeleteReview, imgUrl } from "../../api";
import ReviewStars from "../../components/ReviewStars";

const TABS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "hidden", label: "Hidden" },
  { id: "", label: "All" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return "—"; }
};

const AdminReviews = () => {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = () => {
    setLoading(true);
    adminListReviews(tab || undefined)
      .then((data) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, [tab]);

  const act = async (id, patch, label) => {
    setActing(id);
    try {
      if (patch === "delete") {
        if (!window.confirm("Delete this review permanently? This cannot be undone.")) { setActing(null); return; }
        await adminDeleteReview(id);
      } else {
        await adminUpdateReview(id, patch);
      }
      toast.success(label);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update.");
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="p-8 lg:p-12">
      <h1 className="font-serif-display text-4xl text-[var(--espresso)]">Reviews</h1>
      <p className="text-sm text-[var(--espresso)]/60 mt-2">Approve, hide or delete customer reviews. Only approved reviews are shown on the site.</p>

      <div className="flex flex-wrap gap-2 mt-8">
        {TABS.map((t) => (
          <button
            key={t.id || "all"}
            onClick={() => setTab(t.id)}
            data-testid={`admin-reviews-tab-${t.id || "all"}`}
            className={`px-5 h-10 text-xs tracking-[0.14em] uppercase border transition-colors ${tab === t.id ? "bg-[var(--espresso)] text-white border-[var(--espresso)]" : "border-[var(--sand)] hover:border-[var(--copper)]"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--copper)]" /></div>
      ) : items.length === 0 ? (
        <div className="py-24 text-center text-[var(--espresso)]/50">
          <p className="font-serif-display text-2xl">Nothing here.</p>
          <p className="text-sm mt-2">New reviews land in Pending first.</p>
        </div>
      ) : (
        <div className="mt-8 space-y-6">
          {items.map((r) => (
            <article key={r.id} data-testid={`admin-review-${r.id}`} className="bg-white border border-[var(--sand)] p-6 lg:p-8">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <ReviewStars value={r.rating} size={16} />
                    <span className={`inline-block px-2 py-0.5 text-[10px] tracking-widest uppercase ${r.status === "approved" ? "bg-[var(--copper)] text-white" : r.status === "hidden" ? "bg-red-100 text-red-800" : "bg-[var(--sand)]"}`}>{r.status}</span>
                  </div>
                  {r.title && <h3 className="font-serif-display text-xl text-[var(--espresso)]">{r.title}</h3>}
                  <p className="text-[var(--espresso)]/80 leading-relaxed mt-2 whitespace-pre-line">{r.text}</p>
                  <div className="mt-4 flex items-center flex-wrap gap-x-5 gap-y-1 text-xs text-[var(--espresso)]/60">
                    <span><strong className="text-[var(--espresso)]">{r.name}</strong> · {r.email}</span>
                    <span>Submitted {fmtDate(r.created_at)}</span>
                    {r.approved_at && <span>Approved {fmtDate(r.approved_at)}</span>}
                  </div>
                  {r.photos && r.photos.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {r.photos.map((src) => (
                        <a key={src} href={imgUrl(src)} target="_blank" rel="noopener noreferrer" className="w-20 h-20 bg-[var(--sand)] overflow-hidden">
                          <img src={imgUrl(src)} alt="" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                <div className="w-full lg:w-72 lg:border-l lg:border-[var(--sand)] lg:pl-6 shrink-0">
                  <div className="flex items-center gap-3 mb-4">
                    {r.product_image && (
                      <div className="w-14 h-16 bg-[var(--sand)] overflow-hidden flex-shrink-0">
                        <img src={imgUrl(r.product_image)} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-serif-display text-base text-[var(--espresso)] truncate">{r.product_name}</p>
                      <a href={`/product/${r.product_slug || r.product_id}`} target="_blank" rel="noopener noreferrer" className="text-[11px] tracking-[0.14em] uppercase text-[var(--copper)] inline-flex items-center gap-1">
                        View <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {r.status !== "approved" && (
                      <button
                        onClick={() => act(r.id, { status: "approved" }, "Review approved")}
                        disabled={acting === r.id}
                        data-testid={`approve-${r.id}`}
                        className="flex items-center gap-1.5 px-3 h-9 text-[11px] tracking-[0.14em] uppercase bg-[var(--copper)] text-white hover:bg-[var(--copper-dark)] disabled:opacity-50"
                      >
                        <Check className="w-3.5 h-3.5" /> Approve
                      </button>
                    )}
                    {r.status !== "hidden" && (
                      <button
                        onClick={() => act(r.id, { status: "hidden" }, "Review hidden")}
                        disabled={acting === r.id}
                        data-testid={`hide-${r.id}`}
                        className="flex items-center gap-1.5 px-3 h-9 text-[11px] tracking-[0.14em] uppercase border border-[var(--sand)] hover:border-[var(--espresso)] disabled:opacity-50"
                      >
                        <EyeOff className="w-3.5 h-3.5" /> Hide
                      </button>
                    )}
                    <button
                      onClick={() => act(r.id, "delete", "Review deleted")}
                      disabled={acting === r.id}
                      data-testid={`delete-${r.id}`}
                      className="flex items-center gap-1.5 px-3 h-9 text-[11px] tracking-[0.14em] uppercase text-red-700 border border-red-200 hover:border-red-500 disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
